/**
 * KycGate — Centralized KYC verification gate for all investment actions.
 *
 * Provides:
 *   - useKycGate()    — hook returning { requireKyc, KycGateModal }
 *   - KycInvestButton — drop-in Button that auto-gates on KYC status
 *
 * Usage:
 *   const { requireKyc, KycGateModal } = useKycGate();
 *   <Button onClick={() => requireKyc(() => openInvestFlow())}>Invest Now</Button>
 *   <KycGateModal />
 *
 * Any new offering or property page just imports useKycGate() and wraps
 * invest actions with requireKyc(). The modal renders itself.
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { Button } from './ui/button';
import { useUser } from '../../contexts/UserContext';

// ---------------------------------------------------------------------------
// Hook — useKycGate
// ---------------------------------------------------------------------------

export function useKycGate() {
  const { canInvest, kycStatus, user } = useUser();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);

  /**
   * Wrap any invest action with this function.
   * If KYC is verified, executes the callback immediately.
   * If not, shows the KYC required modal and blocks the action.
   *
   * @returns true if the action was allowed, false if blocked
   */
  const requireKyc = useCallback(
    (onAllowed?: () => void): boolean => {
      const check = canInvest();
      if (check.canInvest) {
        onAllowed?.();
        return true;
      }
      setShowModal(true);
      return false;
    },
    [canInvest],
  );

  /** Modal component — render once in your page, it handles its own visibility */
  const KycGateModal = useCallback(
    () =>
      showModal ? (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
          <div className="bg-card rounded-xl p-6 max-w-md w-full border border-border shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-amber-500" />
              </div>
              <h3 className="text-lg font-semibold">Complete Investor Verification</h3>
            </div>
            <p className="text-muted-foreground mb-2">
              Additional investor verification is required before this investment can proceed.
              Offering document access and investment eligibility are reviewed separately.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              Current verification status:{' '}
              <span className="font-medium text-amber-500 capitalize">
                {(user?.investment_eligibility_status || kycStatus || 'not_started').replace(/_/g, ' ')}
              </span>
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowModal(false)}>
                Close
              </Button>
              <Button
                className="flex-1"
                onClick={() => {
                  setShowModal(false);
                  navigate('/portal/settings?tab=kyc');
                }}
              >
                Continue Verification
              </Button>
            </div>
          </div>
        </div>
      ) : null,
    [showModal, kycStatus, navigate, user?.investment_eligibility_status],
  );

  return { requireKyc, KycGateModal, showModal, setShowModal };
}

// ---------------------------------------------------------------------------
// Component — KycInvestButton
// ---------------------------------------------------------------------------

interface KycInvestButtonProps {
  /** Called only when user passes KYC check */
  onInvest: () => void;
  /** Button label (default: 'Invest Now') */
  label?: string;
  /** Additional classes */
  className?: string;
  /** Button size */
  size?: 'default' | 'sm' | 'lg' | 'icon';
  /** Disable regardless of KYC */
  disabled?: boolean;
}

/**
 * Drop-in invest button that automatically gates on KYC status.
 * Renders a standard Button + the KYC modal when needed.
 * Use this for any new offering or property detail page.
 */
export function KycInvestButton({
  onInvest,
  label = 'Invest Now',
  className = '',
  size = 'lg',
  disabled = false,
}: KycInvestButtonProps) {
  const { requireKyc, KycGateModal } = useKycGate();

  return (
    <>
      <Button
        className={className}
        size={size}
        disabled={disabled}
        onClick={() => requireKyc(onInvest)}
      >
        {label}
      </Button>
      <KycGateModal />
    </>
  );
}

// ---------------------------------------------------------------------------
// Component — KycMobileCTA
// ---------------------------------------------------------------------------

interface KycMobileCTAProps {
  /** Called only when user passes KYC check */
  onInvest: () => void;
  /** CTA label */
  label?: string;
}

/**
 * Mobile sticky CTA with built-in KYC gate.
 * Drop-in replacement for MobileCTA on invest pages.
 */
export function KycMobileCTA({ onInvest, label = 'Invest Now' }: KycMobileCTAProps) {
  const { requireKyc, KycGateModal } = useKycGate();

  return (
    <>
      <div className="lg:hidden fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-border z-40">
        <Button className="w-full h-12" onClick={() => requireKyc(onInvest)}>
          {label}
        </Button>
      </div>
      <KycGateModal />
    </>
  );
}
